import type { CSSProperties } from 'react';
import type { VehicleType } from '../lib/types';

/**
 * The three Zoomo Rides vehicles as one family of side-view vector drawings (120×72, facing right, ground at y 64):
 * aqua #5CE1E6 bodies with deep aqua #0A8A94 / #1FB5C0 shading, Night #0D0D0D canopies and tyres. They read the
 * same on dark and light (a hairline edge keeps the black parts off a dark tile). Drawn after the mockup's renders,
 * not copied from them.
 */
const AQUA = '#5CE1E6';
const DEEP = '#0A8A94';
const MID = '#1FB5C0';
const NIGHT = '#0D0D0D';
const EDGE = 'var(--vehEdge, rgba(255,255,255,0.14))';
const GLASS = '#1B2425';
const RIM = '#CFE3E4';

function Wheel({ cx, cy, r, spin }: { cx: number; cy: number; r: number; spin?: boolean }) {
  return (
    <g>
      <circle cx={cx} cy={cy} r={r} fill={NIGHT} stroke={EDGE} strokeWidth={1} />
      <circle cx={cx} cy={cy} r={r * 0.5} fill={RIM} />
      <g className={spin ? 'zr-wheel' : undefined}>
        <path d={`M${cx - r * 0.42} ${cy}H${cx + r * 0.42}M${cx} ${cy - r * 0.42}V${cy + r * 0.42}`} stroke={NIGHT} strokeWidth={1.3} strokeLinecap="round" opacity={0.55} />
      </g>
      <circle cx={cx} cy={cy} r={r * 0.16} fill={NIGHT} />
    </g>
  );
}

/** Auto-rickshaw: black canopy, aqua body, two riders in the back and the driver up front. */
function Auto({ spin, riders = true }: { spin?: boolean; riders?: boolean }) {
  return (
    <>
      {/* canopy */}
      <path d="M15 41V18Q15 8 27 8H84Q93 8 97 14Q101 22 105 41Z" fill={NIGHT} stroke={EDGE} strokeWidth={1} strokeLinejoin="round" />
      <path d="M22 38V19.5Q22 15 26.5 15H57V38Z" fill={GLASS} />
      <path d="M62 38V15H85Q89 15 90 19L94.5 38Z" fill={GLASS} />
      {riders && (
        <g>
          {/* two riders on the back seat, the driver in front */}
          <rect x="27" y="25" width="9" height="13" rx="3.5" fill="#F5F5F3" />
          <circle cx="31.5" cy="21.5" r="3.3" fill="#8A5A3C" />
          <path d="M28.2 20.6Q31.5 16.6 34.8 20.6Z" fill="#2B2322" />
          <rect x="40" y="25.5" width="9" height="12.5" rx="3.5" fill="#E2557F" />
          <circle cx="44.5" cy="22" r="3.2" fill="#A86E4C" />
          <path d="M41.2 21.4Q44.5 17 47.8 21.4L47.6 24Q44.5 21.6 41.4 24Z" fill="#2B2322" />
          <rect x="70" y="24.5" width="9" height="13.5" rx="3.5" fill="#EFE3C8" />
          <circle cx="74.5" cy="21" r="3.2" fill="#8A5A3C" />
          <path d="M71.2 20.2Q74.5 16.4 77.8 20.2Z" fill="#2B2322" />
          <path d="M80 30L88 28.5" stroke={NIGHT} strokeWidth={1.6} strokeLinecap="round" />
        </g>
      )}
      {/* body */}
      <path d="M13 53Q13 41.5 23 40H83Q92 40 99 45.5L107.5 51.5Q111 54.2 109.4 57.6L106.5 61.5H17Q13 61.5 13 57.5Z" fill={AQUA} />
      <path d="M13 54.5H110Q110.4 56.6 109.4 57.6L106.5 61.5H17Q13 61.5 13 57.5Z" fill={MID} />
      <path d="M21 47.5H97" stroke={NIGHT} strokeWidth={2} strokeLinecap="round" />
      <rect x="15" y="39" width="88" height="2.6" rx="1.3" fill={DEEP} />
      <rect x="104" y="47.5" width="4.5" height="3.6" rx="1.2" fill="#FFFFFF" />
      <rect x="13.4" y="45" width="2.6" height="4" rx="1" fill="#FF8A65" />
      <Wheel cx={31} cy={61} r={8.6} spin={spin} />
      <Wheel cx={95} cy={61} r={7.8} spin={spin} />
    </>
  );
}

/** Motorbike: aqua tank and tail, black seat, dark engine, light rims. */
function Bike({ spin }: { spin?: boolean }) {
  return (
    <>
      <path d="M30 56L57 49" stroke={NIGHT} strokeWidth={3.6} strokeLinecap="round" />
      <path d="M92 56L83.5 25" stroke={NIGHT} strokeWidth={3.8} strokeLinecap="round" />
      <path d="M77 21.5H88" stroke={NIGHT} strokeWidth={3.2} strokeLinecap="round" />
      <path d="M18 54Q20 42 32 42Q40 42 42.5 48" fill="none" stroke={MID} strokeWidth={4} strokeLinecap="round" />
      <path d="M84 50Q88 43.5 98.5 45.5" fill="none" stroke={MID} strokeWidth={3.6} strokeLinecap="round" />
      <rect x="47" y="42.5" width="24" height="13" rx="3.5" fill="#2A3233" />
      <path d="M52 47H66M52 51H66" stroke="#4A5658" strokeWidth={1.4} strokeLinecap="round" />
      <path d="M24 44Q28 37.5 39 36.5H57V43.5Q45 45 34.5 50Z" fill={AQUA} />
      <path d="M53 37Q57 28.5 69 28H79Q86 28.5 86 35L84 41.5Q70 44.5 54 42.5Z" fill={AQUA} />
      <path d="M56 41.5Q70 43.5 84.5 40.6L84 41.5Q70 44.5 54 42.5Z" fill={DEEP} />
      <path d="M35 36.8Q37 30.5 47 30.5H62Q67 30.5 64.5 34.5L36 38Z" fill={NIGHT} stroke={EDGE} strokeWidth={1} />
      <path d="M38 54.5H62" stroke="#9AA6A8" strokeWidth={3} strokeLinecap="round" />
      <rect x="21.5" y="42.5" width="3.6" height="3.4" rx="1" fill="#FF8A65" />
      <circle cx="88.5" cy="30" r="3.6" fill="#FFFFFF" stroke={NIGHT} strokeWidth={1.5} />
      <Wheel cx={30} cy={56} r={10.5} spin={spin} />
      <Wheel cx={92} cy={56} r={10.5} spin={spin} />
    </>
  );
}

/** Car: a compact hatchback with dark glass (the owner's third type is "car", so no taxi roof sign). */
function Car({ spin }: { spin?: boolean }) {
  return (
    <>
      <path d="M8 51Q8 41 18 39.5L34 37.5L46 25Q50 21 58 21H80Q88 21 94 28.5L101.5 37Q112 38.5 113 47V53Q113 57 108.5 57H12.5Q8 57 8 53Z" fill={AQUA} />
      <path d="M8 50.5H113V53Q113 57 108.5 57H12.5Q8 57 8 53Z" fill={MID} />
      <path d="M38 37L49 26Q51 24 56 24H66V37Z" fill={GLASS} />
      <path d="M70 37V24H80Q86 24 90 29.5L95.5 37Z" fill={GLASS} />
      <path d="M52 26.5L44 35" stroke="#FFFFFF" strokeWidth={1.4} strokeLinecap="round" opacity={0.18} />
      <path d="M68 38V51" stroke={DEEP} strokeWidth={1.2} />
      <path d="M58 42H63M76 42H81" stroke={DEEP} strokeWidth={1.6} strokeLinecap="round" />
      <rect x="107" y="42" width="5" height="3.6" rx="1.2" fill="#FFFFFF" />
      <rect x="8.4" y="42.5" width="3" height="4" rx="1" fill="#FF8A65" />
      <Wheel cx={30} cy={57} r={9.4} spin={spin} />
      <Wheel cx={90} cy={57} r={9.4} spin={spin} />
    </>
  );
}

export function VehicleArt({ type, width, height, spin, shadow = true, riders, style, className }: { type: VehicleType; width: number; height?: number; spin?: boolean; shadow?: boolean; riders?: boolean; style?: CSSProperties; className?: string }) {
  const h = height ?? Math.round((width * 72) / 120);
  return (
    <svg aria-hidden="true" width={width} height={h} viewBox="0 0 120 72" className={className} style={{ display: 'block', flex: 'none', overflow: 'visible', ...style }}>
      {shadow && <ellipse cx="60" cy="68" rx="48" ry="3.6" fill="var(--vehShadow, rgba(0,0,0,0.28))" />}
      {type === 'BIKE' ? <Bike spin={spin} /> : type === 'CAR' ? <Car spin={spin} /> : <Auto spin={spin} riders={riders} />}
    </svg>
  );
}
